import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { PermissionService } from '@ipg/ngx-frontend-toolset';
import { ClientNameRef } from '../../models/clients.model';
import { Group } from '../../models/group.model';
import { GroupsService } from '../../services/groups.service';
import { ClientsService } from '../clients/services/clients.service';
import { AuditLog } from './audit-logs.model';
import { AuditLogsService } from './services/audit-logs.service';

const HIGHLIGHT_STYLE =
  'background-color:rgb(253 224 71/0.3);display:inline-block;width:calc(100% + 1.5rem);margin:0 -0.75rem;padding:0 0.75rem;font-weight:600;';

@Component({
  selector: 'app-audit-log-detail-dialog',
  imports: [
    MatDialogModule,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    DatePipe,
    RouterLink,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .diff-pre {
      white-space: pre-wrap;
      word-break: break-all;
      scrollbar-width: thin;
      max-height: 28rem;
    }
  `,
  template: `
    <h2 mat-dialog-title class="flex items-center gap-2">
      <span class="text-sm font-medium px-3 py-1 rounded-full bg-gray-100">{{
        logData().resource
      }}</span>
      <span
        class="text-sm font-medium px-3 py-1 rounded-full bg-secondary-100 text-secondary-800"
        >{{ logData().action }}</span
      >
      @if (logData().sensitive) {
        <span
          class="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
        >
          <mat-icon class="size-3.5! text-sm! leading-none!">lock</mat-icon>
          Gevoelig
        </span>
      }
    </h2>

    <mat-dialog-content class="flex flex-col gap-4">
      <div class="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <div>
          <span class="text-gray-500">Gebruiker</span>
          <p class="font-medium">
            {{ logData().metadata['display_name'] || '—' }}
            @if (logData().metadata['username']) {
              <span class="text-gray-400">#{{ logData().metadata['username'] }}</span>
            }
          </p>
        </div>
        <div>
          <span class="text-gray-500">Datum</span>
          <p class="font-medium">
            {{ logData().createdAt | date: 'dd MMMM yyyy, HH:mm:ss' }}
          </p>
        </div>
        <div>
          <span class="text-gray-500">Status</span>
          <p class="font-medium">{{ logData().status }}</p>
        </div>
        <div>
          <span class="text-gray-500">IP-adres</span>
          <p class="font-medium">{{ logData().ipAddress || '—' }}</p>
        </div>
      </div>

      @if (logData().isShielded) {
        <div class="p-4 rounded-xl bg-amber-50/70 border border-amber-200 flex flex-col gap-3">
          <div class="flex items-start gap-3">
            <div class="p-2 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <mat-icon class="size-5! text-xl! leading-none!">shield</mat-icon>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-sm font-semibold text-amber-900">Gevoelige gegevens afgeschermd</span>
              <p class="text-xs text-amber-800 leading-relaxed">
                Dit auditlogboek bevat gevoelige gegevens en is standaard afgeschermd ter bescherming van de privacy.
              </p>
            </div>
          </div>

          @if (canReadSensitive) {
            <div class="pt-2 border-t border-amber-200/60 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <span class="text-xs text-amber-700 italic">
                Het inzien van deze gegevens wordt geregistreerd in het auditlogboek.
              </span>
              <button
                mat-flat-button
                color="primary"
                [disabled]="isUnshielding()"
                (click)="unshield()"
                class="shrink-0"
              >
                @if (isUnshielding()) {
                  <mat-spinner diameter="18" class="mr-2"></mat-spinner>
                  <span>Gegevens laden...</span>
                } @else {
                  <ng-container>
                    <mat-icon class="size-4! text-base! mr-1.5 leading-none!">lock_open</mat-icon>
                    <span>Gevoelige gegevens inzien</span>
                  </ng-container>
                }
              </button>
            </div>
          } @else {
            <div class="pt-2 border-t border-amber-200/60">
              <span class="text-xs text-amber-700">
                U heeft niet de benodigde rechten om deze gegevens in te zien.
              </span>
            </div>
          }
        </div>
      } @else {
        @if (clientIds().length) {
          <div class="flex flex-col gap-1">
            <span class="text-xs uppercase tracking-wider font-medium text-gray-500"
              >Cliënten</span
            >
            <div class="flex flex-col gap-0.5 text-sm mt-1">
              @for (id of clientIds(); track id) {
                @if (clientsById().get(id); as client) {
                  @if (canViewClient) {
                    <a
                      [routerLink]="['/clients', id]"
                      mat-dialog-close
                      class="text-secondary-700 hover:underline font-medium w-fit"
                    >
                      {{ client.name }}
                      <span class="text-gray-400">({{ client.idAlias }})</span>
                    </a>
                  } @else {
                    <span class="font-medium"
                      >{{ client.name }}
                      <span class="text-gray-400">({{ client.idAlias }})</span></span
                    >
                  }
                } @else {
                  <span class="font-mono text-xs text-gray-500">{{ id }}</span>
                }
              }
            </div>
          </div>
        }

        @if (groupIds().length) {
          <div class="flex flex-col gap-1">
            <span class="text-xs uppercase tracking-wider font-medium text-gray-500"
              >Mappen</span
            >
            <div class="flex flex-col gap-0.5 text-sm mt-1">
              @for (id of groupIds(); track id) {
                @if (groupsById().get(id); as group) {
                  @if (canViewGroup) {
                    <a
                      [routerLink]="groupRouterLink()"
                      [queryParams]="groupQueryParams(id)"
                      mat-dialog-close
                      class="text-secondary-700 hover:underline font-medium w-fit"
                    >
                      {{ group.label }}
                      <span class="text-gray-400">({{ id }})</span>
                    </a>
                  } @else {
                    <span class="font-medium">{{ group.label }}
                      <span class="text-gray-400">({{ id }})</span></span>
                  }
                } @else {
                  <span class="font-mono text-xs text-gray-500">{{ id }}</span>
                }
              }
            </div>
          </div>
        }

        @if (otherDetails().length) {
          <div class="flex flex-col gap-1">
            <span class="text-xs uppercase tracking-wider font-medium text-gray-500"
              >Overige gegevens</span
            >
            <div class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm mt-1">
              @for (item of otherDetails(); track item.label) {
                <span class="text-gray-500">{{ item.label }}</span>
                <span class="font-medium break-all">{{ item.value }}</span>
              }
            </div>
          </div>
        }

        @if (logData().oldValue || logData().newValue) {
          <div
            class="grid gap-x-6 mt-6"
            [class.grid-cols-2]="logData().oldValue && logData().newValue"
            [class.grid-cols-1]="!logData().oldValue || !logData().newValue"
          >
            @if (logData().oldValue) {
              <div class="flex flex-col">
                <span class="text-xs uppercase tracking-wider font-medium text-gray-500"
                  >Oude waarde</span
                >
                <pre
                  class="diff-pre flex-1 mt-1 p-3 bg-red-50 border border-red-200 rounded-lg text-xs overflow-auto select-none"
                  [innerHTML]="highlightedOldJson()"
                ></pre>
              </div>
            }

            @if (logData().newValue) {
              <div class="flex flex-col">
                <span class="text-xs uppercase tracking-wider font-medium text-gray-500"
                  >Nieuwe waarde</span
                >
                <pre
                  class="diff-pre flex-1 mt-1 p-3 bg-green-50 border border-green-200 rounded-lg text-xs overflow-auto select-none"
                  [innerHTML]="highlightedNewJson()"
                ></pre>
              </div>
            }
          </div>
        }
      }
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Sluiten</button>
    </mat-dialog-actions>
  `,
})
export class AuditLogDetailDialogComponent {
  private readonly _initialData: AuditLog = inject(MAT_DIALOG_DATA);
  protected readonly logData = signal<AuditLog>(this._initialData);
  protected readonly isUnshielding = signal<boolean>(false);

  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _clientsService = inject(ClientsService);
  private readonly _groupsService = inject(GroupsService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _auditLogsService = inject(AuditLogsService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _destroyRef = inject(DestroyRef);

  /**
   * metadata keys rendered in their own sections (acting user, resolved
   * clients, resolved media groups) and therefore excluded from the generic
   * "other details" list.
   */
  private static readonly _HANDLED_META_KEYS = new Set([
    'display_name',
    'username',
    'client_id',
    'client_ids',
    'group_ids',
  ]);

  /** Human labels for the remaining ("other") view-detail metadata keys. */
  private static readonly _META_LABELS: Record<string, string> = {
    session_id: 'Sessie ID',
    agent_name: 'AI Agent',
    message_id: 'Bericht ID',
    model: 'AI Model',
    rounds: 'Aantal AI-rondes',
    tool_names: 'Gebruikte tools',
    truncated: 'Ingekort',
    contact_ids: 'Contacten',
    media: 'Media',
    event_id: 'Afspraak',
    event_ids: 'Afspraken',
    scm_id: 'Signalering',
    scm_ids: 'Signaleringen',
    keyword: 'Zoekterm',
    q: 'Zoekterm',
    target_audit_log_id: 'Audit ID',
    target_resource: 'Doel-resource',
    target_action: 'Doel-actie',
  };

  /** Whether the viewer may view sensitive audit logs. */
  protected readonly canReadSensitive =
    this._permissionService.hasPermission('audit_log:read_sensitive');

  /** Whether the viewer may open a client's detail page (gates the link). */
  protected readonly canViewClient =
    this._permissionService.hasPermission('client:read');

  /** Whether the viewer may open a media group/folder (gates the link). */
  protected readonly canViewGroup =
    this._permissionService.hasPermission('group:read');

  /** Client ids referenced by this entry (single `client_id` + `client_ids`). */
  protected readonly clientIds = computed<string[]>(() => {
    const metadata = this.logData().metadata ?? {};
    const ids = new Set<string>();

    const single = metadata['client_id'];
    if (typeof single === 'string') {
      ids.add(single);
    }
    const many = metadata['client_ids'];
    if (Array.isArray(many)) {
      for (const value of many) {
        if (typeof value === 'string') {
          ids.add(value);
        }
      }
    }

    return [...ids];
  });

  private readonly _clientsById = signal<Map<string, ClientNameRef>>(new Map());
  /** Resolved client id → name/alias (only those the viewer may see). */
  protected readonly clientsById = this._clientsById.asReadonly();

  /** Media-group (folder) ids referenced by this entry (`group_ids`). */
  protected readonly groupIds = computed<string[]>(() => {
    const many = (this.logData().metadata ?? {})['group_ids'];
    return Array.isArray(many)
      ? many.filter((value): value is string => typeof value === 'string')
      : [];
  });

  private readonly _groupsById = signal<Map<string, Group>>(new Map());
  /** Resolved group id → label (only those the viewer may see). */
  protected readonly groupsById = this._groupsById.asReadonly();

  constructor() {
    this._loadRelatedEntities();
  }

  private _loadRelatedEntities(): void {
    const clientIds = this.clientIds();
    if (clientIds.length && this.canViewClient) {
      this._clientsService
        .getByIds(clientIds)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (clients) =>
            this._clientsById.set(new Map(clients.map((c) => [c._id, c]))),
          error: () => this._clientsById.set(new Map()),
        });
    }

    const groupIds = this.groupIds();
    if (groupIds.length && this.canViewGroup) {
      this._groupsService
        .getByIds(groupIds)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (groups) =>
            this._groupsById.set(new Map(groups.map((g) => [g._id, g]))),
          error: () => this._groupsById.set(new Map()),
        });
    }
  }

  protected unshield(): void {
    if (this.isUnshielding() || !this.canReadSensitive) {
      return;
    }
    this.isUnshielding.set(true);
    this._auditLogsService
      .unshield(this.logData()._id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (unshieldedLog) => {
          this.logData.set(unshieldedLog);
          this.isUnshielding.set(false);
          this._loadRelatedEntities();
        },
        error: () => {
          this.isUnshielding.set(false);
          this._snackBar.open(
            'Kon gevoelige gegevens niet ophalen. Probeer het opnieuw.',
            'Sluiten',
            { duration: 5000 }
          );
        },
      });
  }

  /** Build the routerLink segments for group links. */
  protected groupRouterLink(): string[] {
    const clientId = this.logData().metadata?.['client_id'];
    if (typeof clientId === 'string') {
      return ['/clients', clientId, 'media'];
    }
    return ['/media'];
  }

  /** Build query params for group links — only include `view=shared` for shared entries. */
  protected groupQueryParams(id: string): Record<string, string> {
    const params: Record<string, string> = { groupId: id };
    if (this.logData().action === 'view_shared') {
      params['view'] = 'shared';
    }
    return params;
  }

  /**
   * Remaining view metadata (everything that isn't the acting user or a
   * resolved client) rendered as readable label/value rows — e.g. viewed media
   * filenames, event/scm ids, or a search term.
   */
  protected readonly otherDetails = computed<{ label: string; value: string }[]>(
    () => {
      const metadata = this.logData().metadata ?? {};
      const rows: { label: string; value: string }[] = [];

      for (const [key, raw] of Object.entries(metadata)) {
        if (AuditLogDetailDialogComponent._HANDLED_META_KEYS.has(key)) {
          continue;
        }
        rows.push({
          label: AuditLogDetailDialogComponent._META_LABELS[key] ?? key,
          value: this._formatMetaValue(raw),
        });
      }

      return rows;
    }
  );

  private _formatMetaValue(raw: unknown): string {
    if (raw === null || raw === undefined) {
      return '—';
    }
    if (Array.isArray(raw)) {
      if (raw.length === 0) {
        return '—';
      }
      return raw
        .map((item) =>
          this._isRecord(item)
            ? String(
                item['filename'] ?? item['label'] ?? item['id'] ?? JSON.stringify(item)
              )
            : String(item)
        )
        .join(', ');
    }
    if (this._isRecord(raw)) {
      return JSON.stringify(raw);
    }
    return String(raw);
  }

  private readonly _changedKeys = computed<Set<string>>(() => {
    const oldVal = this.logData().oldValue;
    const newVal = this.logData().newValue;

    if (!this._isRecord(oldVal) || !this._isRecord(newVal)) {
      return new Set();
    }

    const changed = new Set<string>();
    const allKeys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);

    for (const key of allKeys) {
      const inOld = key in oldVal;
      const inNew = key in newVal;
      if (
        !inOld ||
        !inNew ||
        JSON.stringify(oldVal[key]) !== JSON.stringify(newVal[key])
      ) {
        changed.add(key);
      }
    }

    return changed;
  });

  protected readonly highlightedOldJson = computed(() => {
    const html = this._highlightJson(this.logData().oldValue, this._changedKeys());
    return this._sanitizer.bypassSecurityTrustHtml(html);
  });

  protected readonly highlightedNewJson = computed(() => {
    const html = this._highlightJson(this.logData().newValue, this._changedKeys());
    return this._sanitizer.bypassSecurityTrustHtml(html);
  });

  /**
   * Renders JSON with inline-styled highlights on changed top-level keys.
   * Tracks brace/bracket depth so nested objects/arrays under a changed
   * key are fully highlighted.
   */
  private _highlightJson(value: unknown, changedKeys: Set<string>): string {
    const json = JSON.stringify(value, null, 2);

    if (changedKeys.size === 0) {
      return this._escapeHtml(json);
    }

    const lines = json.split('\n');
    const result: string[] = [];

    let insideChangedKey = false;
    let depth = 0;

    for (const line of lines) {
      // Check if this line starts a new top-level key (indent = 2 spaces exactly)
      const topLevelKeyMatch = line.match(/^ {2}"([^"]+)":/);

      if (topLevelKeyMatch) {
        // We're at a top-level key — reset tracking
        insideChangedKey = changedKeys.has(topLevelKeyMatch[1]);
        depth = 0;

        // Check if the value opens a nested structure on this same line
        const afterColon = line.slice(line.indexOf(':') + 1).trim();
        if (afterColon === '{' || afterColon === '[') {
          depth = 1;
        }
      } else if (insideChangedKey && depth > 0) {
        // Track nested depth for multi-line values
        for (const ch of line) {
          if (ch === '{' || ch === '[') depth++;
          if (ch === '}' || ch === ']') depth--;
        }

        // If depth drops to 0, this is the closing line of the nested value
        if (depth <= 0) {
          result.push(
            `<span style="${HIGHLIGHT_STYLE}">${this._escapeHtml(line)}</span>`
          );
          insideChangedKey = false;
          depth = 0;
          continue;
        }
      } else if (topLevelKeyMatch === null && !insideChangedKey) {
        // Not inside a changed key and not a top-level key line
      }

      const escaped = this._escapeHtml(line);
      result.push(
        insideChangedKey
          ? `<span style="${HIGHLIGHT_STYLE}">${escaped}</span>`
          : escaped
      );
    }

    return result.join('\n');
  }

  private _escapeHtml(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private _isRecord(val: unknown): val is Record<string, unknown> {
    return val !== null && typeof val === 'object' && !Array.isArray(val);
  }
}

